import { type CompanyDto, type MeDto, type RoleDto, suggestLogin, type UserDto, userCreateSchema } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Checkbox, Group, Modal, MultiSelect, PasswordInput, SegmentedControl, Stack, Switch, Table, Text, TextInput } from '@mantine/core';
import { IconFingerprint, IconPlus, IconShieldCheck, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import { can } from '../../auth';
import { TradeBadge } from '../../components/Badges';
import { CompanyBadge } from '../../components/CompanyBadge';
import { CompaniesField } from '../../components/CompaniesField';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/ui';
import { QueryState } from '../../components/QueryState';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';

/** Nowy pracownik — konto (logowanie) opcjonalne; resztę danych uzupełnia się na karcie pracownika. */
function NewEmployee({ roles, companies, onDone }: { roles: RoleDto[]; companies: CompanyDto[]; onDone: (id?: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ fullName: '', phone: '', email: '', hasAccount: false, login: '', loginTouched: false, password: '', roleCodes: [] as string[], allCompanies: true, companyIds: [] as string[], isSubcontractor: false, mustChangePassword: true });
  const [errors, setErrors] = useState<string[]>([]);
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const login = f.loginTouched ? f.login : suggestLogin(f.fullName);
  const save = useMutation({
    mutationFn: (body: unknown) => api<UserDto>('/users', { method: 'POST', body }),
    onSuccess: (u) => {
      notifyOk(u.hasAccount ? `Dodano pracownika z kontem — login: ${u.login}` : 'Dodano pracownika');
      void qc.invalidateQueries({ queryKey: ['users'] });
      onDone(u.id);
    },
    onError: notifyError,
  });
  const submit = () => {
    const r = userCreateSchema.safeParse({
      fullName: f.fullName,
      phone: f.phone,
      email: f.email,
      hasAccount: f.hasAccount,
      ...(f.hasAccount ? { login: login || undefined, password: f.password, roleCodes: f.roleCodes, mustChangePassword: f.mustChangePassword } : { roleCodes: [] }),
      allCompanies: f.allCompanies,
      companyIds: f.companyIds,
      isSubcontractor: f.isSubcontractor,
    });
    if (!r.success) return setErrors(r.error.issues.map((i) => i.message));
    setErrors([]);
    save.mutate(r.data);
  };
  return (
    <Stack>
      {errors.length > 0 && (
        <Alert color="red" style={{ whiteSpace: 'pre-line' }}>
          {errors.join('\n')}
        </Alert>
      )}
      <TextInput label="Imię i nazwisko" value={f.fullName} onChange={(e) => set({ fullName: e.currentTarget.value })} data-autofocus required />
      <Group grow wrap="wrap">
        <TextInput label="Telefon" value={f.phone} onChange={(e) => set({ phone: e.currentTarget.value })} />
        <TextInput label="E-mail" value={f.email} onChange={(e) => set({ email: e.currentTarget.value })} />
      </Group>
      <CompaniesField companies={companies} all={f.allCompanies} ids={f.companyIds} onChange={(allCompanies, companyIds) => set({ allCompanies, companyIds })} />
      <Switch label="Podwykonawca" checked={f.isSubcontractor} onChange={(e) => set({ isSubcontractor: e.currentTarget.checked })} />
      <Card withBorder padding="sm">
        <Stack gap="xs">
          <SegmentedControl
            value={f.hasAccount ? 'tak' : 'nie'}
            onChange={(v) => set({ hasAccount: v === 'tak' })}
            data={[
              { value: 'nie', label: 'Bez konta' },
              { value: 'tak', label: 'Z kontem' },
            ]}
          />
          {!f.hasAccount ? (
            <Text size="xs" c="dimmed">
              Pracownik bez loginu i hasła — może być w ekipach i planie prac, ale nie loguje się. Konto można założyć później na karcie pracownika.
            </Text>
          ) : (
            <>
              <Text size="xs" c="dimmed">
                Konto użytkownika — pracownik loguje się do CRM i aplikacji mobilnej.
              </Text>
              <Group grow wrap="wrap" align="flex-start">
                <TextInput label="Login" description="z imienia i nazwiska — można zmienić" value={login} onChange={(e) => set({ login: e.currentTarget.value.toLowerCase(), loginTouched: true })} />
                <PasswordInput label="Hasło początkowe" description="min. 10 znaków" value={f.password} onChange={(e) => set({ password: e.currentTarget.value })} />
              </Group>
              <Checkbox label="Wymagaj zmiany hasła przy pierwszym logowaniu" checked={f.mustChangePassword} onChange={(e) => set({ mustChangePassword: e.currentTarget.checked })} />
              <MultiSelect label="Role (uprawnienia)" data={roles.map((r) => ({ value: r.code, label: r.name }))} value={f.roleCodes} onChange={(v) => set({ roleCodes: v })} />
            </>
          )}
        </Stack>
      </Card>
      <Group justify="flex-end">
        <Button variant="default" onClick={() => onDone()}>
          Anuluj
        </Button>
        <Button onClick={submit} loading={save.isPending}>
          Dodaj pracownika
        </Button>
      </Group>
    </Stack>
  );
}

/** Pracownicy: lista osób (z kontem użytkownika albo bez); szczegóły na karcie pracownika. */
export function EmployeesPage({ me }: { me: MeDto }) {
  // pracownicy.podglad bez zarządzania — lista bez dodawania
  const canManage = can(me, 'uzytkownicy.zarzadzanie');
  const navigate = useNavigate();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<UserDto[]>('/users') });
  const roles = useQuery({ queryKey: ['roles'], queryFn: () => api<RoleDto[]>('/roles') });
  const companies = useQuery({ queryKey: ['companies'], queryFn: () => api<CompanyDto[]>('/companies') });
  const [adding, setAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const roleName = (code: string) => roles.data?.find((r) => r.code === code);
  const list = (users.data ?? []).filter((u) => showInactive || u.active);
  const inactive = (users.data ?? []).filter((u) => !u.active).length;

  return (
    <>
      <PageHeader
        title="Pracownicy"
        description="Osoby pracujące w firmach — z kontem użytkownika (logowanie do CRM i aplikacji) albo bez. Kliknij pracownika, żeby otworzyć jego kartę: dane, uprawnienia zawodowe, funkcje w branżach, konto i telefony."
        actions={
          canManage && (
            <Button leftSection={<IconPlus size={16} />} onClick={() => setAdding(true)} disabled={!roles.data || !companies.data}>
              Nowy pracownik
            </Button>
          )
        }
      />
      {inactive > 0 && <Switch mb="sm" size="xs" label={`Pokaż nieaktywnych (${inactive})`} checked={showInactive} onChange={(e) => setShowInactive(e.currentTarget.checked)} />}
      <Card padding={0}>
        <QueryState isLoading={users.isLoading} error={users.error}>
          <Table.ScrollContainer minWidth={820}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Imię i nazwisko</Table.Th>
                  <Table.Th>Stanowisko</Table.Th>
                  <Table.Th>Kontakt</Table.Th>
                  <Table.Th>Konto</Table.Th>
                  <Table.Th>Firmy</Table.Th>
                  <Table.Th>Ostatnie logowanie</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((u) => (
                  <Table.Tr key={u.id} className="row-link" style={{ opacity: u.active ? 1 : 0.5 }} onClick={() => navigate(`/pracownicy/${u.id}`)}>
                    <Table.Td>
                      <Anchor component={Link} to={`/pracownicy/${u.id}`} size="sm" fw={500} onClick={(e) => e.stopPropagation()}>
                        {u.fullName}
                      </Anchor>{' '}
                      {!u.active && (
                        <Badge color="gray" size="xs">
                          nieaktywny
                        </Badge>
                      )}{' '}
                      {u.isSubcontractor && (
                        <Badge color="grape" size="xs" variant="light">
                          podwykonawca
                        </Badge>
                      )}{' '}
                      {u.tradeCodes.map((c) => (
                        <TradeBadge key={c} code={c} />
                      ))}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.position || '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.phone || (u.email ? '' : '—')}</Text>
                      {u.email && (
                        <Text size="xs" c="dimmed">
                          {u.email}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {u.hasAccount ? (
                        <Group gap={4} wrap="wrap">
                          <Text size="sm" ff="monospace">
                            {u.login}
                          </Text>
                          {u.roleCodes.map((c) => (
                            <Badge key={c} variant="light" size="xs" color="indigo">
                              {roleName(c)?.name ?? c}
                            </Badge>
                          ))}
                          {u.mfaEnabled && (
                            <Badge color="blue" size="xs" variant="light" leftSection={<IconShieldCheck size={10} />} title="Logowanie dwuetapowe włączone">
                              2FA
                            </Badge>
                          )}
                          {u.passkeys > 0 && (
                            <Badge color="teal" size="xs" variant="light" leftSection={<IconFingerprint size={10} />} title="Logowanie kluczem dostępu">
                              klucz
                            </Badge>
                          )}
                        </Group>
                      ) : (
                        <Badge color="gray" variant="outline" size="sm">
                          bez konta
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {u.allCompanies ? (
                        <Text size="sm" c="dimmed">
                          wszystkie
                        </Text>
                      ) : (
                        u.companyIds.map((id) => {
                          const c = companies.data?.find((x) => x.id === id);
                          return c ? <CompanyBadge key={id} company={c} /> : null;
                        })
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{u.hasAccount ? formatDateTime(u.lastLoginAt) : '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {users.data && list.length === 0 && (
            <EmptyState
              icon={<IconUsers size={24} />}
              title={users.data.length ? 'Wszyscy pracownicy są nieaktywni' : 'Brak pracowników'}
              hint={users.data.length ? 'Włącz „Pokaż nieaktywnych”, żeby ich zobaczyć.' : 'Dodaj pierwszego pracownika — konto użytkownika jest opcjonalne.'}
              action={
                canManage && (
                  <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)} disabled={!roles.data || !companies.data}>
                    Nowy pracownik
                  </Button>
                )
              }
            />
          )}
        </QueryState>
      </Card>
      <Modal opened={adding} onClose={() => setAdding(false)} title="Nowy pracownik" size="lg">
        {adding && roles.data && companies.data && (
          <NewEmployee
            roles={roles.data}
            companies={companies.data}
            onDone={(id) => {
              setAdding(false);
              if (id) navigate(`/pracownicy/${id}`);
            }}
          />
        )}
      </Modal>
    </>
  );
}
